/**
 * usePaddlePrice Hook
 *
 * Fetches localized pricing information from Paddle for display in the UI.
 * Handles multi-currency support by using Paddle's PricePreview API.
 *
 * Features:
 * - Fetches localized price in user's currency ($, €, £)
 * - Calculates discounted price based on percentage discount
 * - Returns formatted price strings ready for display
 * - Caches results to avoid redundant API calls
 *
 * @example
 * const {
 *   fullPrice,           // "€9.99"
 *   discountPrice,       // "€4.99"
 *   currencyCode,        // "EUR"
 *   isLoading,
 *   error,
 * } = usePaddlePrice();
 *
 * // Use in UI
 * <Text>Unlock for {isActive ? discountPrice : fullPrice}</Text>
 */

import { useCallback, useEffect, useState } from 'react';
import { PADDLE_CONFIG, initPaddle } from '../lib/paddle';
import logger from '../utils/logger';

// ============================================================================
// Constants
// ============================================================================

const DISCOUNT_PERCENTAGE = 50; // 50% off for limited time offer
// Per-priceId/discount cache key — fixes a bug where a single shared key
// caused all hook instances to read back the first-fetched price regardless
// of their own priceId argument (rendered all SKUs at the same value).
// discountId-aware so we can preview discounted prices alongside standard ones.
const cacheKeyFor = (priceId, discountId) =>
  `paddle_price_cache:${priceId || 'default'}:${discountId || 'none'}`;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour cache

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formats a price with currency symbol.
 * Uses Intl.NumberFormat for proper localization.
 *
 * @param {number} amount - Price amount (e.g., 9.99)
 * @param {string} currencyCode - Currency code (e.g., "EUR", "USD", "GBP")
 * @returns {string} Formatted price (e.g., "€9.99", "$9.99", "£9.99")
 */
const formatPrice = (amount, currencyCode) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    // Fallback for unsupported currencies
    const symbols = {
      EUR: '€',
      USD: '$',
      GBP: '£',
    };
    const symbol = symbols[currencyCode] || currencyCode;
    return `${symbol}${amount.toFixed(2)}`;
  }
};

/**
 * Calculate discounted price.
 *
 * @param {number} fullAmount - Original price amount
 * @param {number} discountPercentage - Discount percentage (e.g., 50)
 * @returns {number} Discounted amount
 */
const calculateDiscountedPrice = (fullAmount, discountPercentage) => {
  const discount = fullAmount * (discountPercentage / 100);
  // Use floor to round down (9.99 * 0.5 = 4.995 → 4.99, not 5.00)
  return Math.floor((fullAmount - discount) * 100) / 100;
};

// ============================================================================
// Main Hook
// ============================================================================

/**
 * usePaddlePrice Hook
 *
 * @param {Object} options - Hook options
 * @param {string} [options.priceId] - Paddle price ID (defaults to Pro price)
 * @param {number} [options.discountPercentage] - Discount percentage (defaults to 50%)
 *
 * @returns {Object} Price information
 *   - fullPrice: string - Formatted full price (e.g., "€9.99")
 *   - discountPrice: string - Formatted discounted price (e.g., "€4.99")
 *   - fullAmount: number - Full price amount (e.g., 9.99)
 *   - discountAmount: number - Discounted amount (e.g., 4.99)
 *   - currencyCode: string - Currency code (e.g., "EUR")
 *   - isLoading: boolean - True while fetching price
 *   - error: Error | null - Error if fetching failed
 *   - refetch: () => void - Manually refetch price
 */
export const usePaddlePrice = ({
  priceId = PADDLE_CONFIG.priceIds?.pro,
  discountPercentage = DISCOUNT_PERCENTAGE,
  discountId = null,
} = {}) => {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceData, setPriceData] = useState({
    // D-041: FALLBACK VALUE — shown only while Paddle preview is loading or if priceId is unconfigured.
    // Do NOT hard-code this as a final price. PricingSection suppresses display when isLoading=true.
    fullAmount: 9.99,
    currencyCode: 'EUR', // Default fallback
  });

  // Derived values
  const discountAmount = calculateDiscountedPrice(priceData.fullAmount, discountPercentage);
  const fullPrice = formatPrice(priceData.fullAmount, priceData.currencyCode);
  const discountPrice = formatPrice(discountAmount, priceData.currencyCode);

  // ============================================================================
  // Fetch Price from Paddle
  // ============================================================================

  const fetchPrice = useCallback(async () => {
    if (!priceId) {
      logger.warn('[usePaddlePrice] No price ID configured');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check session cache first (keyed per priceId + discountId).
      // Cached values fall into two shapes:
      //   - { data: {...}, timestamp }     → successful preview, restore + done.
      //   - { notEligible: true, timestamp} → previously-known incompatible
      //     priceId × discountId combo (Paddle returned 400 with
      //     `transaction_discount_not_eligible`). We mark error and skip the
      //     re-fetch to avoid spamming the console + the network on every render.
      const cacheKey = cacheKeyFor(priceId, discountId);
      if (typeof sessionStorage !== 'undefined') {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.timestamp < CACHE_TTL_MS) {
            if (parsed.notEligible) {
              setError(new Error('discount_not_eligible'));
              setIsLoading(false);
              return;
            }
            logger.log('[usePaddlePrice] Using cached price:', { priceId, data: parsed.data });
            setPriceData(parsed.data);
            setIsLoading(false);
            return;
          }
        }
      }

      // Initialize Paddle
      await initPaddle();

      // Check if Paddle is available
      if (typeof window === 'undefined' || !window.Paddle) {
        throw new Error('Paddle not available');
      }

      // Use Paddle's PricePreview to get localized pricing.
      // If discountId is provided, Paddle returns the post-discount line totals
      // for the first cycle (handles both one-shot and recurring discounts).
      const previewArgs = { items: [{ priceId, quantity: 1 }] };
      if (discountId) {
        if (discountId.startsWith('dsc_')) previewArgs.discountId = discountId;
        else previewArgs.discountCode = discountId;
      }
      const previewResult = await window.Paddle.PricePreview(previewArgs);

      logger.log('[usePaddlePrice] Price preview result:', previewResult);

      if (previewResult?.data?.details?.lineItems?.[0]) {
        const lineItem = previewResult.data.details.lineItems[0];
        const formattedTotals = lineItem.formattedTotals || lineItem.formatted_totals;
        const unitTotals = lineItem.unitTotals || lineItem.unit_totals;

        // Debug: Log all available price fields
        logger.log('[usePaddlePrice] lineItem:', lineItem);
        logger.log('[usePaddlePrice] unitTotals:', unitTotals);
        logger.log('[usePaddlePrice] formattedTotals:', formattedTotals);
        logger.log('[usePaddlePrice] price object:', lineItem.price);

        // Extract price information
        const currencyCode = previewResult.data.currencyCode || previewResult.data.currency_code || 'EUR';

        // Get the numeric amount - Paddle returns amounts in smallest currency unit (cents)
        // Use total for tax-inclusive pricing (price configured in Paddle includes tax)
        // D-041: FALLBACK VALUE — used if Paddle preview response is malformed.
        // PricingSection checks `!skuConfigured || priceInfo?.isLoading` before rendering.
        let fullAmount = 9.99;

        if (unitTotals?.total) {
          // Convert from cents to main currency unit
          fullAmount = parseInt(unitTotals.total, 10) / 100;
        } else if (formattedTotals?.total) {
          // Parse from formatted string as fallback
          const numericString = formattedTotals.total.replace(/[^0-9.,-]/g, '').replace(',', '.');
          // D-041: parseFloat fallback is a sentinel, not a display price; PricingSection suppresses rendering.
          fullAmount = parseFloat(numericString) || 9.99;
        }

        const newPriceData = {
          fullAmount,
          currencyCode,
        };

        // Cache the result (per priceId — see cacheKeyFor)
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: newPriceData,
              timestamp: Date.now(),
            })
          );
        }

        setPriceData(newPriceData);
        logger.log('[usePaddlePrice] Price fetched:', newPriceData);
      } else {
        logger.warn('[usePaddlePrice] Unexpected response structure:', previewResult);
      }
    } catch (err) {
      // Detect Paddle's `transaction_discount_not_eligible` — fired when a
      // discountId is passed against a priceId it doesn't target. Cache the
      // negative so subsequent renders don't re-fire the same doomed call.
      // (Per-user FR codes only target one SKU; the resolver naively returns
      // the same code for every FR-eligible SKU. D-014 follow-up.)
      const code = err?.error?.code || err?.code;
      const isNotEligible = code === 'transaction_discount_not_eligible';
      if (isNotEligible) {
        if (typeof sessionStorage !== 'undefined') {
          try {
            sessionStorage.setItem(
              cacheKeyFor(priceId, discountId),
              JSON.stringify({ notEligible: true, timestamp: Date.now() })
            );
          } catch (storageErr) {
            // ignore quota
          }
        }
        logger.log('[usePaddlePrice] Discount not eligible for price (expected):', { priceId, discountId });
      } else {
        logger.error('[usePaddlePrice] Error fetching price:', err);
      }
      setError(err);
      // Keep default fallback values
    } finally {
      setIsLoading(false);
    }
  }, [priceId, discountId]);

  // Fetch on mount
  useEffect(() => {
    fetchPrice();
  }, [fetchPrice]);

  // ============================================================================
  // Return Hook Value
  // ============================================================================

  return {
    fullPrice,
    discountPrice,
    fullAmount: priceData.fullAmount,
    discountAmount,
    currencyCode: priceData.currencyCode,
    isLoading,
    error,
    refetch: fetchPrice,
  };
};

export default usePaddlePrice;
